/**
 * Veröffentlichungen: Alben, EPs und Singles.
 *
 * Der Einstieg unter „Songs". Die Metapher ist eine andere als im Regal:
 * dort stehen Bücher, hier liegen Notizblöcke – aber die räumliche
 * Organisation ist dieselbe, damit sich beides nach derselben App anfühlt.
 * Gewischt wird mit derselben Physik (ui/swipe-rail.ts), je Art eine Reihe.
 *
 * Songs ohne Veröffentlichung gehen nicht verloren: sie bekommen eine eigene
 * Zeile am Ende, damit man sie findet und zuordnen kann.
 */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import type { Release, ReleaseKind } from '../types.ts';
import { activeProject } from '../db/active.ts';
import {
  RELEASE_KINDS,
  deleteRelease,
  emptyRelease,
  listMyArtworks,
  listReleases,
  listSongs,
  releaseSummary,
  saveRelease,
  unassignedSongs,
  visibleArtwork,
} from '../db/repo.ts';
import { renderArtwork } from '../ui/artwork.ts';
import { createSwipeRail } from '../ui/swipe-rail.ts';
import type { SwipeRail } from '../ui/swipe-rail.ts';
import { askText } from '../ui/ask.ts';
import { SEPARATOR, withContextMenu } from '../ui/context-menu.ts';
import { button, h } from '../ui/dom.ts';
import { icon } from '../ui/icons.ts';
import { toast } from '../ui/toast.ts';
import { exportRelease } from '../features/pdf/dialog.ts';

/** Ein Zeichen je Art, wie ein Aufdruck auf dem Karton. */
function releaseEmblem(kind: ReleaseKind): string {
  if (kind === 'single') return '●';
  if (kind === 'ep') return '◎';
  return '◉';
}

export async function renderReleases(): Promise<View> {
  const project = await activeProject();
  if (!project) {
    navigate('#/projects', true);
    return { title: 'Projekte', body: h('div'), drawing: null };
  }
  const projectId = project.id;

  const [releases, songs, myArtworks] = await Promise.all([listReleases(projectId), listSongs(projectId), listMyArtworks()]);
  const loose = unassignedSongs(songs, releases);

  /** Aufräumen beim Verlassen der Ansicht. */
  const rails: SwipeRail[] = [];

  async function create(kind: ReleaseKind): Promise<void> {
    const label = RELEASE_KINDS.find((k) => k.value === kind)?.label ?? 'Album';
    const title = await askText({
      title: `${label} anlegen`,
      label: 'Titel',
      placeholder: `Wie heisst das ${label}?`,
    });
    if (title === null) return;
    const release = await saveRelease(
      emptyRelease(projectId, {
        title,
        kind,
        order: releases.length,
      }),
    );
    navigate(`#/release/${release.id}`);
  }

  /**
   * Eine Reihe je Art.
   * Sie bekommt dieselbe Wisch-Mechanik wie das Regal – nur so fühlt sich
   * die App wie ein Stück an und nicht wie zwei verschiedene Bildschirme.
   */
  function kindRow(kind: ReleaseKind, label: string, plural: string): HTMLElement {
    const mine = releases.filter((release) => release.kind === kind);

    /*
     * Die Reihe dieser Zeile. Sie entsteht erst weiter unten, wird aber
     * schon von den Blöcken gebraucht: Nach einem Wisch schickt der Browser
     * noch ein click-Ereignis hinterher, und ohne diese Abfrage würde sich
     * am Ende jedes Wisches eine Veröffentlichung öffnen.
     */
    let railApi: SwipeRail | null = null;
    const opened = (id: string) => {
      if (railApi?.swiped()) return;
      navigate(`#/release/${id}`);
    };

    const track = h('div', { class: 'pads__track swipe-rail__track' });
    for (const release of mine) track.append(artworkItem(release, opened));

    // Am Ende der Reihe steht der Platz für einen neuen Block.
    track.append(addItem(kind, label, () => railApi?.swiped() === true));

    const rail = h(
      'div',
      {
        class: 'pads__rail swipe-rail',
        attrs: { tabindex: '0', role: 'group', 'aria-label': plural },
      },
      track,
    );

    railApi = createSwipeRail({
      rail,
      track,
      // Eine Reihe zum Stoebern, kein ausgewaehltes Buch.
      align: 'start',
      // Steht nur der Platzhalter in der Reihe, gibt es nichts zu blättern –
      // dann soll ein Tipper darauf sofort durchkommen.
      ignore: (target) => target.closest('.pads__add') !== null && mine.length === 0,
    });
    rails.push(railApi);

    return h(
      'section',
      { class: 'pads__section' },
      h(
        'header',
        { class: 'pads__head' },
        h('h2', { class: 'pads__title', text: plural }),
        h('span', {
          class: 'pads__count',
          text: mine.length === 0 ? 'noch keine' : `${mine.length}`,
        }),
      ),
      rail,
    );
  }

  function artworkItem(release: Release, open: (id: string) => void): HTMLElement {
    const cover = renderArtwork({
      // Privates Cover sieht nur, wer es gewaehlt hat.
      image: visibleArtwork(release, myArtworks.get(release.id)),
      label: release.title || 'Ohne Titel',
      badge: release.songIds.length > 0 ? String(release.songIds.length) : undefined,
      emblem: releaseEmblem(release.kind),
    });

    const item = h(
      'div',
      {
        class: 'pads__item',
        attrs: { role: 'button', tabindex: '0', 'aria-label': release.title || 'Ohne Titel' },
        on: {
          click: () => open(release.id),
          keydown: (ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              // Die Tastatur wischt nicht, hier darf es direkt gehen.
              navigate(`#/release/${release.id}`);
            }
          },
        },
      },
      cover,
      h('span', { class: 'pads__label', text: release.title || 'Ohne Titel' }),
      h('span', { class: 'pads__meta', text: releaseSummary(release) }),
    );
    return withContextMenu(item, release.title || 'Ohne Titel', () => [
      { label: 'Öffnen', action: () => navigate(`#/release/${release.id}`) },
      { label: 'Als PDF …', action: () => void exportRelease(release) },
      {
        label: 'Umbenennen …',
        action: async () => {
          const title = await askText({ title: 'Umbenennen', label: 'Titel', value: release.title, confirm: 'Speichern' });
          if (title === null) return;
          await saveRelease({ ...release, title });
          refresh();
        },
      },
      SEPARATOR,
      {
        label: 'Löschen …',
        danger: true,
        action: async () => {
          if (!confirm(`"${release.title || 'Ohne Titel'}" löschen?\n\nDie Songs bleiben erhalten.`)) return;
          await deleteRelease(release.id);
          toast('Veröffentlichung gelöscht');
          refresh();
        },
      },
    ]);
  }

  /** Der Platzhalter am Ende jeder Reihe: ein leerer Block mit Plus. */
  function addItem(kind: ReleaseKind, label: string, wasSwiped: () => boolean): HTMLElement {
    return h(
      'div',
      {
        class: 'pads__item pads__item--add',
        attrs: { role: 'button', tabindex: '0', 'aria-label': `${label} anlegen` },
        on: {
          click: () => {
            if (wasSwiped()) return;
            void create(kind);
          },
          keydown: (ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              void create(kind);
            }
          },
        },
      },
      h('div', { class: 'pads__add' }, icon('plus')),
      h('span', { class: 'pads__meta', text: `${label} anlegen` }),
    );
  }

  /* --------------------------------------------- Songs ohne Zuordnung */

  const looseRow = h(
    'section',
    { class: 'pads__section pads__section--loose' },
    h(
      'header',
      { class: 'pads__head' },
      h('h2', { class: 'pads__title', text: 'Einzelne Songs' }),
      h('span', {
        class: 'pads__count',
        text: `${loose.length} ohne Veröffentlichung`,
      }),
    ),
    h(
      'p',
      { class: 'muted' },
      loose.length === 0
        ? 'Jeder Song liegt auf einer Veröffentlichung.'
        : 'Diese Songs gehören noch zu keinem Album, keiner EP und keiner Single.',
    ),
    h(
      'div',
      { class: 'btn-row', style: { marginTop: '12px' } },
      button('Alle Songs öffnen', () => navigate('#/songs'), { class: 'btn' }),
    ),
  );

  const body = h(
    'div',
    { class: 'pads' },
    ...RELEASE_KINDS.map((k) => kindRow(k.value, k.label, k.plural)),
    looseRow,
  );

  // Erst wenn das Layout steht, kann mittig ausgerichtet werden.
  requestAnimationFrame(() => {
    for (const rail of rails) rail.recenter();
  });

  return {
    title: 'Songs',
    subtitle: project.name || 'Ohne Namen',
    actions: [
      h(
        'button',
        {
          class: 'btn btn--icon',
          attrs: { type: 'button', title: 'Alle Songs', 'aria-label': 'Alle Songs' },
          on: { click: () => navigate('#/songs') },
        },
        icon('list'),
      ),
    ],
    body,
    cleanup: () => {
      for (const rail of rails) rail.destroy();
    },
  };
}
